import { Head, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/layouts/PageHeader"
import { CapturesTable } from "@/components/captures/CapturesTable"
import type { Capture } from "@/types"

interface CapturesIndexProps {
  captures: Capture[]
  filter_status?: string
  filter_source_id?: number
  sources: { id: number; name: string }[]
  status_counts: { all: number; pending: number; delivered: number; failed: number }
}

const STATUS_TABS = [
  { key: undefined, label: "All", countKey: "all" as const },
  { key: "pending", label: "Pending", countKey: "pending" as const },
  { key: "delivered", label: "Delivered", countKey: "delivered" as const },
  { key: "failed", label: "Failed", countKey: "failed" as const },
]

function buildUrl(status?: string, sourceId?: number) {
  const params = new URLSearchParams()
  if (status) params.set("status", status)
  if (sourceId) params.set("source_id", String(sourceId))
  const qs = params.toString()
  return `/admin/captures${qs ? `?${qs}` : ""}`
}

export default function Index({
  captures,
  filter_status,
  filter_source_id,
  sources,
  status_counts,
}: CapturesIndexProps) {
  return (
    <>
      <Head title="Captures" />
      <div className="space-y-6">
        <PageHeader title="Captures" description="Browse all captured feed items." />

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <Button
                key={tab.label}
                variant={filter_status === tab.key ? "default" : "outline"}
                size="sm"
                onClick={() => router.visit(buildUrl(tab.key, filter_source_id))}
              >
                {tab.label} ({status_counts[tab.countKey]})
              </Button>
            ))}
          </div>

          {sources.length > 0 && (
            <Select
              value={filter_source_id ? String(filter_source_id) : "all"}
              onValueChange={(value) => {
                const sourceId = value === "all" ? undefined : Number(value)
                router.visit(buildUrl(filter_status, sourceId))
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {sources.map((source) => (
                  <SelectItem key={source.id} value={String(source.id)}>
                    {source.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <CapturesTable captures={captures} showSource />
      </div>
    </>
  )
}
